import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { path, partial } from 'ramda';

import { Table } from 'components';

import {
  resetRowsSelection,
  changeRowSelection,
  updateSorting,
  initTable,
  toggleExpandableRow,
} from './actions';

/**
 * SmartTable automatically connects to the store by passed path.
 * Optionally you can pass config through props instead of keeping it in the store.
 * For example:
 *   <SmartTable pathToState="path.to.state" />
 * will try to get table data from state like this:
 *    {
 *      path: {
 *        to: {
 *          state: {
 *            config: {
                columns: [{
                  name: 'path.to.translation',
                  dataField: 'myField',
                },
                ...],
                uniqueIdField: 'id',
                selectionField: 'selected',
                nameAsTranslationKey: true,
                useCheckboxSelection: true,
                sortable: true,
              },
              isAllRowsSelected: false,
              sortingInfo: {},
              data: [{
                myField: 'myData',
                ...,
              },
              ...],
 *          },
 *          ...
 *        },
 *        ...
 *      },
 *      ...
 *    }
 *
 * Where:
 * {object}   config                      - Table configuration object.
 * {Column[]} config.columns              - List of columns configuration.
 *
 * {Column}   column
 * {string}   column.name                 - Will be presented at column header.
 * {string}   column.dataField            - Connects column with a field from 'data' object.
 * {string}   column.expandable           - If true, each click by cell of this column
 *                                          will toggle expandable row below this cell.
 * {string}   column.width                - Sets column width.
 * {function} column.transformer          - Specifies a function tht transforms table data.
 * {boolean}  column.hidden               - If true, the column will be hidden.
 * {boolean}  column.hideOnMobile         - If true, the column will be hidden only on
 *                                          mobile devices.
 *
 * {string}   config.uniqueIdField        - Field which contains unique Id for each row.
 * {string}   config.selectionField       - Row field to store selection state. Checkbox will be
 *                                          presented only if row[selectionField] has boolean type.
 *                                          Works only if config.useCheckboxSelection is true.
 * {boolean}  config.nameAsTranslationKey - If true, each column.name will be interpreted as
 *                                          translation key and will be translated using I18n.
 * {boolean}  config.useCheckboxSelection - If true, add column with checkboxes to select rows.
 * {boolean}  config.sortable             - If true, add sorting logic to the table.
 * {function} config.comparator           - Specifies a function that defines the sort order.
 *                                          Works only if config.sortable is true.
 * {string}   config.expandField          - Row field to store expansion state.
 *                                          Works only if at least one column.expandable
 *                                          property is true.
 *
 * {boolean}  isAllRowsSelected           - Stores state of 'select all' checkbox. Works only if
 *                                          config.useCheckboxSelection is true.
 * {object}   sortingInfo                 - Stores state of sorting (field and direction).
 *                                          Works only if config.sortable is true.
 * {Data[]}   data                        - Stores table data.
 */
class SmartTable extends PureComponent {
  UNSAFE_componentWillMount() {
    this.props.actions.initTable(this.props.pathToState, this.props.config);
  }

  onToggleExpandableRow = (data) => {
    this.props.actions.toggleExpandableRow(
      this.props.pathToState,
      this.props.config,
      data,
    );

    if (this.props.onToggleExpandableRow) {
      this.props.onToggleExpandableRow(data);
    }
  };

  render() {
    const {
      data,
      sortingInfo,
      config,
      isAllRowsSelected,
      actions,
      pathToState,
      expandRenderer,
    } = this.props;

    const onResetRowsSelection = partial(
      actions.resetRowsSelection,
      [pathToState, config],
    );
    const onChangeRowSelection = partial(
      actions.changeRowSelection,
      [pathToState, config],
    );
    const onUpdateSorting = partial(
      actions.updateSorting,
      [pathToState, config],
    );

    if (!data || !config) {
      return null;
    }

    return (
      <Table
        data={data}
        sortingInfo={sortingInfo}
        config={config}
        isAllRowsSelected={isAllRowsSelected}
        resetRowsSelection={onResetRowsSelection}
        changeRowSelection={onChangeRowSelection}
        updateSorting={onUpdateSorting}
        toggleExpandableRow={this.onToggleExpandableRow}
        expandRenderer={expandRenderer}
        {...this.props}
      />
    );
  }
}

function mapStateToProps(state, { pathToState, config = {} }) {
  const tableState = pathToState
    ? path(pathToState.split('.'), state) : {};

  return {
    ...tableState,
    config: {
      ...tableState.config,
      ...config,
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({
      resetRowsSelection,
      changeRowSelection,
      updateSorting,
      initTable,
      toggleExpandableRow,
    }, dispatch),
  };
}

SmartTable.propTypes = {
  data: PropTypes.array,
  sortingInfo: PropTypes.object,
  config: PropTypes.object,
  isAllRowsSelected: PropTypes.bool,
  actions: PropTypes.object,
  pathToState: PropTypes.string,
  expandRenderer: PropTypes.func,
  onToggleExpandableRow: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(SmartTable);
